import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import axios, { API } from "@/lib/api";
import { formatApiError } from "@/lib/apiError";
import { Loader2, CreditCard, Plus, X } from "lucide-react";

// Static price lookup for the "Buy seats" hint (kept in lockstep with
// backend's PLAN_PRESETS; if you change one, update the other).
const PLAN_PRICES = { studio: 0, team: 599, enterprise: 799 };

function Stat({ label, value, testId }) {
  return (
    <div className="rounded-lg border border-white/8 bg-[#141414] px-3 py-2.5">
      <div className="text-[10px] uppercase tracking-[0.18em] text-white/45">{label}</div>
      <div className="text-sm text-white mt-0.5 tabular-nums" data-testid={testId}>{value}</div>
    </div>
  );
}

/**
 * Enterprise Phase 3 — Billing tab.
 *
 * Shows current plan tier, seat usage (X/Y), cycle status badge,
 * next-renewal quote, and three actions:
 *   - Renew now (creates checkout → if stub mode, auto-confirm; else
 *     launches Razorpay Checkout via the same loaded SDK as billing.py).
 *   - Buy/Remove extra seats (immediate seat capacity change, settled
 *     at next cycle).
 *   - Switch plan (Team ↔ Enterprise).
 */
export default function TeamBillingTab({ workspace, onChanged }) {
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(null);   // 'renew' | 'seats' | 'plan'
  const [seatCount, setSeatCount] = useState(1);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/workspaces/${workspace.id}/billing`);
      setSummary(r.data);
    } catch (err) {
      toast.error(formatApiError(err, "Could not load billing."));
    } finally {
      setLoading(false);
    }
  }, [workspace.id]);

  useEffect(() => { load(); }, [load]);

  // ---------- Renew flow ----------
  const renew = async () => {
    setBusy("renew");
    try {
      const ck = await axios.post(`${API}/workspaces/${workspace.id}/billing/checkout`);
      const d = ck.data;
      if (d.stub) {
        await axios.post(`${API}/workspaces/${workspace.id}/billing/confirm-stub`,
                         { invoice_id: d.invoice_id });
        toast.success(`Renewed (stub) — cycle active.`);
        await load();
        await onChanged?.();
        return;
      }
      if (!window.Razorpay) {
        await new Promise((res, rej) => {
          const tag = document.createElement("script");
          tag.src = "https://checkout.razorpay.com/v1/checkout.js";
          tag.onload = res; tag.onerror = rej;
          document.body.appendChild(tag);
        });
      }
      const rzp = new window.Razorpay({
        key: d.key_id, amount: d.amount_paise, currency: d.currency,
        name: d.name, description: d.description, order_id: d.order_id,
        prefill: d.prefill,
        handler: async (resp) => {
          try {
            await axios.post(`${API}/workspaces/${workspace.id}/billing/verify`, {
              invoice_id: d.invoice_id,
              order_id: resp.razorpay_order_id,
              payment_id: resp.razorpay_payment_id,
              signature: resp.razorpay_signature,
            });
            toast.success(`Renewed — cycle active.`);
            await load(); await onChanged?.();
          } catch (err) {
            toast.error(formatApiError(err, "Verify failed."));
          }
        },
        modal: { ondismiss: () => toast("Checkout cancelled.") },
        theme: { color: "#D4AF37" },
      });
      rzp.open();
    } catch (err) {
      toast.error(formatApiError(err, "Checkout failed."));
    } finally {
      setBusy(null);
    }
  };

  // ---------- Adjust seats (add or remove) ----------
  const buySeats = async () => {
    if (seatCount === 0) return;
    setBusy("seats");
    try {
      await axios.post(`${API}/workspaces/${workspace.id}/billing/seats`,
                        { count: seatCount });
      const abs = Math.abs(seatCount);
      const verb = seatCount > 0 ? "Added" : "Removed";
      toast.success(`${verb} ${abs} seat${abs === 1 ? "" : "s"} · settled at next renewal.`);
      setSeatCount(1);
      await load(); await onChanged?.();
    } catch (err) {
      toast.error(formatApiError(err, "Seat adjustment failed."));
    } finally {
      setBusy(null);
    }
  };

  // ---------- Switch plan ----------
  const changePlan = async (plan) => {
    setBusy("plan");
    try {
      await axios.post(`${API}/workspaces/${workspace.id}/billing/plan`, { plan });
      toast.success(`Switched to ${plan} plan.`);
      await load(); await onChanged?.();
    } catch (err) {
      toast.error(formatApiError(err, "Plan change failed."));
    } finally {
      setBusy(null);
    }
  };

  if (loading || !summary) {
    return (
      <div className="max-w-5xl mt-8 text-sm text-white/55">
        <Loader2 className="w-4 h-4 inline mr-2 animate-spin text-[#D4AF37]" /> Loading billing…
      </div>
    );
  }

  const ws = summary.workspace;
  const q  = summary.quote;
  const altPlan = ws.plan === "team" ? "enterprise" : "team";
  const altLabel = ws.plan === "team" ? "Upgrade to Enterprise" : "Switch to Team";
  const lapsed = !ws.cycle_active && ws.billable;

  return (
    <div className="max-w-5xl mt-8 space-y-6" data-testid="team-billing-tab">
      {/* Hero — plan + cycle */}
      <div className={`rounded-2xl border p-6 sm:p-7 ${lapsed ? "border-rose-400/30 bg-gradient-to-br from-rose-500/8 to-[#0B0B0B]" : "border-[#D4AF37]/30 bg-gradient-to-br from-[#D4AF37]/8 via-[#1A1306] to-[#0B0B0B]"}`}>
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <div className="text-[10px] uppercase tracking-[0.22em] text-[#D4AF37]">Current plan</div>
            <h2 className="font-display text-2xl text-white mt-1" data-testid="team-billing-plan-name">{ws.plan_name}</h2>
            <div className="text-xs text-white/55 mt-1">
              ₹{q.base_price_inr.toLocaleString("en-IN")}/cycle · {ws.seats_included} included seats · {ws.monthly_credits} credits/cycle
            </div>
          </div>
          <div className="text-right">
            <div className="text-[10px] uppercase tracking-[0.22em] text-white/45">Cycle</div>
            {lapsed ? (
              <div className="text-sm text-rose-300 mt-1" data-testid="team-billing-status-lapsed">
                Lapsed — renew to unlock allocations
              </div>
            ) : ws.cycle_active ? (
              <div className="text-sm text-emerald-300 mt-1" data-testid="team-billing-status-active">
                Active{ws.current_cycle_end ? ` · renews ${new Date(ws.current_cycle_end).toLocaleDateString("en-IN", { day: "numeric", month: "short" })}` : ""}
              </div>
            ) : (
              <div className="text-sm text-white/65 mt-1" data-testid="team-billing-status-never">
                Not started yet
              </div>
            )}
          </div>
        </div>

        {/* Seat usage */}
        <div className="mt-5 grid grid-cols-2 sm:grid-cols-4 gap-3 text-sm">
          <Stat label="Members" value={`${summary.members_used} / ${ws.seats_total}`} testId="team-billing-stat-members" />
          <Stat label="Extra seats" value={ws.seats_purchased} testId="team-billing-stat-extra" />
          <Stat label="Next invoice" value={`₹${q.amount_inr.toLocaleString("en-IN")}`} testId="team-billing-stat-next" />
          <Stat label="Plan" value={ws.plan_name} testId="team-billing-stat-plan" />
        </div>

        <div className="mt-5 flex flex-wrap gap-2">
          <button
            type="button"
            onClick={renew}
            disabled={busy === "renew"}
            data-testid="team-billing-renew-btn"
            className="inline-flex items-center gap-2 rounded-full bg-[#D4AF37] text-black text-sm font-medium px-5 py-2.5 hover:bg-[#F3CE5A] transition disabled:opacity-50"
          >
            {busy === "renew" ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <CreditCard className="w-3.5 h-3.5" />}
            {ws.cycle_active ? "Renew next cycle" : "Activate workspace"} · ₹{q.amount_inr.toLocaleString("en-IN")}
          </button>
          <button
            type="button"
            onClick={() => changePlan(altPlan)}
            disabled={busy === "plan"}
            data-testid="team-billing-change-plan-btn"
            className="inline-flex items-center gap-2 rounded-full border border-white/15 text-white/85 text-sm px-4 py-2.5 hover:border-[#D4AF37]/40 hover:text-white transition disabled:opacity-50"
          >
            {altLabel}
          </button>
        </div>
      </div>

      {/* Adjust seats */}
      <div className="rounded-2xl border border-white/10 bg-[#0E0E0E] p-6" data-testid="team-billing-seats-panel">
        <div className="flex items-end justify-between gap-4 flex-wrap">
          <div>
            <div className="text-[10px] uppercase tracking-[0.22em] text-white/45">Adjust seats</div>
            <div className="font-display text-lg text-white mt-1">Add or remove extra seats</div>
            <div className="text-xs text-white/55 mt-1">
              ₹{(PLAN_PRICES[ws.plan] || 599).toLocaleString("en-IN")}/seat/cycle · billed at next renewal.
              <span className="text-white/35"> Currently {ws.seats_purchased} extra seat{ws.seats_purchased === 1 ? "" : "s"}.</span>
            </div>
          </div>
          <div className="flex items-center gap-1">
            <button
              type="button"
              onClick={() => setSeatCount((c) => Math.max(-50, c - 1))}
              data-testid="team-billing-seat-dec"
              className="w-9 h-9 rounded-l-md bg-[#141414] border border-white/10 text-white/65 hover:text-white hover:bg-white/[0.06]"
            >−</button>
            <input
              type="number"
              min={-50}
              max={50}
              value={seatCount}
              onChange={(e) => setSeatCount(parseInt(e.target.value, 10) || 0)}
              data-testid="team-billing-seat-input"
              className="w-16 bg-[#141414] border-y border-white/10 px-2 py-2 text-sm text-white text-center focus:outline-none focus:border-[#D4AF37]"
            />
            <button
              type="button"
              onClick={() => setSeatCount((c) => Math.min(50, c + 1))}
              data-testid="team-billing-seat-inc"
              className="w-9 h-9 rounded-r-md bg-[#141414] border border-white/10 text-white/65 hover:text-white hover:bg-white/[0.06]"
            >+</button>
            <button
              type="button"
              onClick={buySeats}
              disabled={busy === "seats" || seatCount === 0}
              data-testid="team-billing-buy-seats-btn"
              className={`ml-2 inline-flex items-center gap-2 rounded-full text-sm font-medium px-4 py-2 transition disabled:opacity-50 ${
                seatCount < 0
                  ? "bg-rose-500/85 text-white hover:bg-rose-500"
                  : "bg-[#D4AF37] text-black hover:bg-[#F3CE5A]"
              }`}
            >
              {busy === "seats"
                ? <Loader2 className="w-3.5 h-3.5 animate-spin" />
                : (seatCount < 0 ? <X className="w-3.5 h-3.5" /> : <Plus className="w-3.5 h-3.5" />)}
              {seatCount < 0
                ? `Remove ${Math.abs(seatCount)} seat${Math.abs(seatCount) === 1 ? "" : "s"}`
                : seatCount > 0
                  ? `Add ${seatCount} seat${seatCount === 1 ? "" : "s"}`
                  : "Adjust seats"}
            </button>
          </div>
        </div>
      </div>

      {/* Invoices */}
      {summary.invoices.length > 0 && (
        <div className="rounded-2xl border border-white/10 bg-[#0E0E0E] p-6">
          <div className="text-[10px] uppercase tracking-[0.22em] text-white/45 mb-3">Recent invoices</div>
          <div className="space-y-2" data-testid="team-billing-invoices">
            {summary.invoices.map((inv) => (
              <div key={inv.id} className="flex items-center justify-between text-sm border-b border-white/5 last:border-0 py-2">
                <div>
                  <div className="text-white/85">{inv.plan === "enterprise" ? "Enterprise" : "Team"} renewal{inv.extra_seats > 0 ? ` + ${inv.extra_seats} extra seats` : ""}</div>
                  <div className="text-[10.5px] text-white/40">{new Date(inv.created_at).toLocaleString("en-IN")}</div>
                </div>
                <div className="text-right">
                  <div className="text-white tabular-nums">₹{inv.amount_inr.toLocaleString("en-IN")}</div>
                  <div className={`text-[10px] uppercase tracking-[0.18em] ${inv.status === "captured" ? "text-emerald-300" : inv.status === "failed" ? "text-rose-300" : "text-white/40"}`}>
                    {inv.status}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
